import { ErrorOutline } from '@mui/icons-material';
import { InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';

const nullableControlValue = value => (value === null || value === undefined ? '' : value);

export const AUTH_IMPORT_DATA_TYPES = {
  BOOLEAN: 'Boolean',
  COUNTRY_TYPE: 'Country Type',
  DATE: 'Date',
  EMPLOYEE_TYPE: 'Employee Type',
  MOVE_TYPE: 'Move Type',
  NUMBER: 'Number',
  OWNERSHIP_TYPE: 'Ownership Type',
  PROVINCE_TYPE: 'Province Type',
  TEMP_FILE_STATUS_TYPE: 'Temp File Status Type',
  TEXT: 'Text',
};

const createAuthImportField = (id, fieldId, fieldName, fieldKey, isSystemRequired = false, dataType = AUTH_IMPORT_DATA_TYPES.TEXT) => ({
  id,
  fieldId,
  fieldName,
  fieldKey,
  dataType,
  isSystemRequired,
  position: null,
  positionError: null,
});

// Intake fields are only part of templates that create intake records, where they are required
const createAuthImportIntakeField = (id, fieldId, fieldName, fieldKey, dataType = AUTH_IMPORT_DATA_TYPES.TEXT) => ({
  ...createAuthImportField(id, fieldId, fieldName, fieldKey, true, dataType),
  isIntakeField: true,
});

export const AUTH_IMPORT_SECTIONS = [
  {
    sectionKey: 'authorizationInfo',
    sectionName: 'Authorization Info',
    fields: [
      createAuthImportIntakeField(56, 76, 'Relocation Counselor', 'relocationCounselorId'),
      createAuthImportField(1, 21, 'First Name', 'firstName', true),
      createAuthImportField(2, 23, 'Middle Name', 'middleName'),
      createAuthImportField(3, 22, 'Last Name', 'lastName', true),
      createAuthImportField(4, 24, 'Nick Name', 'nickName'),
      createAuthImportField(5, 55, 'Is VIP', 'isVIP', false, AUTH_IMPORT_DATA_TYPES.BOOLEAN),
      createAuthImportField(6, 25, 'Spouse First Name', 'spouseFirstName'),
      createAuthImportField(7, 26, 'Spouse Last Name', 'spouseLastName'),
      createAuthImportField(8, 27, 'Notes', 'notes'),
      createAuthImportField(9, 50, 'Client Code', 'clientRecordId', true, AUTH_IMPORT_DATA_TYPES.NUMBER),
      createAuthImportField(10, 51, 'Division Code', 'divisionId', true),
      createAuthImportField(11, 53, 'Policy Code', 'policyId', true),
      createAuthImportField(12, 52, 'File Status', 'tempFileStatusId', false, AUTH_IMPORT_DATA_TYPES.TEMP_FILE_STATUS_TYPE),
      createAuthImportField(13, 49, 'Employee Type', 'employeeTypeId', false, AUTH_IMPORT_DATA_TYPES.EMPLOYEE_TYPE),
      createAuthImportField(14, 54, 'Move Type', 'moveTypeId', false, AUTH_IMPORT_DATA_TYPES.MOVE_TYPE),
      createAuthImportField(15, 48, 'Effective Date', 'effectiveDate', false, AUTH_IMPORT_DATA_TYPES.DATE),
    ],
  },
  {
    sectionKey: 'contactInfo',
    sectionName: 'Contact Info',
    fields: [
      createAuthImportField(16, 28, 'Home Phone', 'homePhone'),
      createAuthImportField(17, 29, 'Work Phone', 'workPhone'),
      createAuthImportField(18, 30, 'Mobile Phone', 'mobilePhone', true),
      createAuthImportField(19, 31, 'Personal Email', 'personalEmail'),
      createAuthImportField(20, 32, 'Work Email', 'workEmail', true),
      createAuthImportField(21, 33, 'Spouse Email', 'spouseEmail'),
    ],
  },
  {
    sectionKey: 'departureAddress',
    sectionName: 'Departure Address',
    fields: [
      createAuthImportField(22, 40, 'Ownership Type', 'departureOwnershipTypeId', false, AUTH_IMPORT_DATA_TYPES.OWNERSHIP_TYPE),
      createAuthImportField(23, 34, 'Address 1', 'departureAddressLine1', true),
      createAuthImportField(24, 35, 'Address 2', 'departureAddressLine2'),
      createAuthImportField(25, 36, 'City', 'departureCity', true),
      createAuthImportField(26, 38, 'Province', 'departureProvinceId', true, AUTH_IMPORT_DATA_TYPES.PROVINCE_TYPE),
      createAuthImportField(27, 37, 'Zip Code', 'departureZipcode'),
      createAuthImportField(28, 39, 'Country', 'departureCountryId', true, AUTH_IMPORT_DATA_TYPES.COUNTRY_TYPE),
    ],
  },
  {
    sectionKey: 'destinationAddress',
    sectionName: 'Destination Address',
    fields: [
      createAuthImportField(29, 47, 'Ownership Type', 'destinationOwnershipTypeId', false, AUTH_IMPORT_DATA_TYPES.OWNERSHIP_TYPE),
      createAuthImportField(30, 41, 'Address 1', 'destinationAddressLine1'),
      createAuthImportField(31, 42, 'Address 2', 'destinationAddressLine2'),
      createAuthImportField(32, 43, 'City', 'destinationCity', true),
      createAuthImportField(33, 45, 'Province', 'destinationProvinceId', true, AUTH_IMPORT_DATA_TYPES.PROVINCE_TYPE),
      createAuthImportField(34, 44, 'Zip Code', 'destinationZipcode'),
      createAuthImportField(35, 46, 'Country', 'destinationCountryId', true, AUTH_IMPORT_DATA_TYPES.COUNTRY_TYPE),
    ],
  },
  {
    // Fields come from the template's client at runtime (see createClientCustomField)
    sectionKey: 'customFields',
    sectionName: 'Custom Fields',
    isClientCustomFieldSection: true,
    fields: [],
  },
];

// Every client custom field mapping uses this field and identifies the client's field by customFieldCommonId
export const CLIENT_CUSTOM_FIELD_ID = 77;

const CLIENT_CUSTOM_FIELD_KEY_PREFIX = 'clientCustomField-';

export const getClientCustomFieldKey = customFieldCommonId => `${CLIENT_CUSTOM_FIELD_KEY_PREFIX}${customFieldCommonId}`;

export const getCustomFieldCommonIdFromKey = fieldKey => Number(fieldKey.slice(CLIENT_CUSTOM_FIELD_KEY_PREFIX.length));

export const isClientCustomFieldKey = fieldKey => fieldKey.startsWith(CLIENT_CUSTOM_FIELD_KEY_PREFIX);

export const isClientCustomFieldMapping = mapping => Number(mapping?.fieldId) === CLIENT_CUSTOM_FIELD_ID;

export const createClientCustomField = ({ customFieldCommonId, fieldName, dataType, isUnavailable }) => ({
  id: getClientCustomFieldKey(customFieldCommonId),
  fieldId: CLIENT_CUSTOM_FIELD_ID,
  fieldKey: getClientCustomFieldKey(customFieldCommonId),
  fieldName,
  dataType,
  customFieldCommonId,
  isSystemRequired: false,
  isUnavailable,
  sectionKey: 'customFields',
  sectionName: 'Custom Fields',
  position: null,
  positionError: null,
});

export const AUTH_IMPORT_FIELDS = AUTH_IMPORT_SECTIONS.reduce((fields, section) => [
  ...fields,
  ...section.fields.map(field => ({
    ...field,
    sectionKey: section.sectionKey,
    sectionName: section.sectionName,
  })),
], []);

export const AUTH_IMPORT_FIELDS_BY_FIELD_ID = new Map(AUTH_IMPORT_FIELDS.map(field => [String(field.fieldId), field]));

export const AUTH_IMPORT_INTAKE_FIELD_KEYS = AUTH_IMPORT_FIELDS.filter(field => field.isIntakeField).map(field => field.fieldKey);

export const getInitialAuthImportMappings = () => AUTH_IMPORT_FIELDS.reduce((mappings, field) => ({
  ...mappings,
  [field.fieldKey]: {
    ...field,
    position: null,
    positionError: null,
  },
}), {});

export const getPositionError = value => {
  if (value === '') return null;

  const position = Number(value);
  return Number.isFinite(position) && position > 0
    ? null
    : 'Position must be a number greater than zero';
};

export const getPositionValue = value => {
  if (value === '') return null;

  const position = Number(value);
  return Number.isFinite(position) && position > 0 ? position : value;
};

export const getAdditionalFields = section => section.fields.filter(field => !field.isSystemRequired);

// Client custom fields have no initial mapping entry, so rows merge the field definition with whatever mapping state exists
export const getVisibleSectionRows = (section, selectedAdditionalFieldKeys, mappingsByFieldKey, createIntakeRecords = false) => section.fields.filter(field => (
  field.isIntakeField
    ? createIntakeRecords
    : field.isSystemRequired || selectedAdditionalFieldKeys.includes(field.fieldKey)
)).map(field => ({
  ...field,
  ...mappingsByFieldKey[field.fieldKey],
}));

export const getAuthImportFieldByFieldId = fieldId => AUTH_IMPORT_FIELDS_BY_FIELD_ID.get(String(fieldId));

export const authImportMappingTableProps = {
  muiTableProps: {
    sx: {
      tableLayout: 'fixed',
      width: '100%',
    },
  },
  muiTableBodyCellProps: {
    sx: {
      '& .MuiFormControl-root': {
        minWidth: 0,
      },
      '& .MuiInputBase-root': {
        minWidth: 0,
      },
    },
  },
  enableEditing: false,
  enableRowActions: false,
  enablePagination: false,
  enableBottomToolbar: false,
  enableTopToolbar: false,
  enableColumnActions: false,
  enableColumnFilters: false,
  enableGlobalFilter: false,
  enableDensityToggle: false,
  enableFullScreenToggle: false,
  enableHiding: false,
};

export const getAuthImportFieldLabel = field => (field.isUnavailable ? `${field.fieldName} (no longer available)` : field.fieldName);

export const getAuthImportColumnMappings = props => {
  const { onPositionChange } = props;

  return [
    {
      accessorKey: 'fieldName',
      header: 'Field Name',
      Cell: ({ row }) => (
        <Typography variant="body2" color={row.original.isUnavailable ? 'error' : 'inherit'}>
          {getAuthImportFieldLabel(row.original)}
        </Typography>
      ),
    },
    {
      accessorKey: 'position',
      header: 'Position',
      Cell: ({ row }) => (
        <TextField
          error={Boolean(row.original.positionError)}
          fullWidth
          size="small"
          value={nullableControlValue(row.original.position)}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          InputProps={{
            endAdornment: row.original.positionError ? (
              <InputAdornment position="end">
                <Tooltip title={row.original.positionError}>
                  <ErrorOutline color="error" fontSize="small" />
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
          onChange={event => onPositionChange(row.original.fieldKey, event.target.value)}
        />
      ),
    },
    {
      accessorKey: 'isSystemRequired',
      header: 'Required By',
      Cell: ({ row }) => (row.original.isSystemRequired ? 'System' : 'Template'),
    },
    {
      accessorKey: 'dataType',
      header: 'Data Type',
    },
  ];
};

getAuthImportColumnMappings.propTypes = {
  onPositionChange: PropTypes.func.isRequired,
};

export const authImportTemplateDisplayColumns = [
  {
    accessorKey: 'fieldName',
    header: 'Field Name',
    enableSorting: false,
    Cell: ({ row }) => {
      if (row.original.isSectionHeader) {
        return (
          <Typography variant="subtitle2">
            {row.original.sectionName}
          </Typography>
        );
      }

      return row.original.isUnavailable ? (
        <Typography variant="body2" color="error">
          {getAuthImportFieldLabel(row.original)}
        </Typography>
      ) : row.original.fieldName;
    },
  },
  {
    accessorKey: 'position',
    header: 'Position',
    enableSorting: false,
    Cell: ({ row }) => (row.original.isSectionHeader ? '' : row.original.position),
  },
  {
    accessorKey: 'isSystemRequired',
    header: 'Required By',
    enableSorting: false,
    Cell: ({ row }) => {
      if (row.original.isSectionHeader) return '';

      return row.original.isSystemRequired ? 'System' : 'Template';
    },
  },
  {
    accessorKey: 'dataType',
    header: 'Data Type',
    enableSorting: false,
    Cell: ({ row }) => (row.original.isSectionHeader ? '' : row.original.dataType),
  },
];